import { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, Shield } from 'lucide-react';

function classStyle(cls) {
  switch (cls?.toLowerCase()) {
    case 'pathogenic': return { bg: '#991b1b', text: '#fff', ring: '#ef4444', label: 'P' };
    case 'likely pathogenic': return { bg: '#c2410c', text: '#fff', ring: '#f97316', label: 'LP' };
    case 'uncertain significance': return { bg: '#4b5563', text: '#fff', ring: '#9ca3af', label: 'VUS' };
    case 'likely benign': return { bg: '#166534', text: '#fff', ring: '#4ade80', label: 'LB' };
    case 'benign': return { bg: '#14532d', text: '#fff', ring: '#22c55e', label: 'B' };
    default: return { bg: '#374151', text: '#fff', ring: '#9ca3af', label: '?' };
  }
}

function strengthStyle(strength) {
  switch (strength) {
    case 'Very Strong': return { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5', badge: '#ef4444' };
    case 'Strong': return { bg: '#fff7ed', text: '#9a3412', border: '#fdba74', badge: '#f97316' };
    case 'Moderate': return { bg: '#fffbeb', text: '#92400e', border: '#fde68a', badge: '#f59e0b' };
    case 'Supporting': return { bg: '#fefce8', text: '#713f12', border: '#fef08a', badge: '#eab308' };
    default: return { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', badge: '#94a3b8' };
  }
}

function CriterionCard({ criterion }) {
  const s = strengthStyle(criterion.strength);
  return (
    <div
      className={`rounded-xl border p-4 flex items-start gap-3 ${!criterion.met ? 'opacity-40' : ''}`}
      style={{ background: s.bg, borderColor: s.border }}
    >
      <div className="shrink-0 mt-0.5">
        {criterion.met
          ? <CheckCircle size={18} style={{ color: s.badge }} />
          : <XCircle size={18} className="text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-sm font-mono" style={{ color: s.text }}>{criterion.code}</span>
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
            style={{ background: s.badge }}
          >
            {criterion.strength}
          </span>
          {!criterion.met && (
            <span className="text-xs text-gray-400 italic">Not met</span>
          )}
        </div>
        <p className="text-xs text-gray-700 mt-1 leading-relaxed">{criterion.description}</p>
      </div>
    </div>
  );
}

export default function ACMGClassification({ acmg }) {
  const [expanded, setExpanded] = useState(false);
  const c = classStyle(acmg.classification);
  const metCount = acmg.criteria.filter((x) => x.met).length;

  return (
    <section id="acmg" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-white/70" />
          <h3 className="text-white font-bold text-base">ACMG/AMP Classification</h3>
        </div>
        <span className="text-white/50 text-xs">{metCount}/{acmg.criteria.length} criteria met</span>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row gap-6 items-start mb-6">
          {/* Large badge */}
          <div className="flex flex-col items-center gap-2 shrink-0">
            <div
              className="w-24 h-24 rounded-2xl flex items-center justify-center text-4xl font-extrabold shadow-lg"
              style={{ background: c.bg, color: c.text, boxShadow: `0 0 0 4px ${c.ring}40` }}
            >
              {c.label}
            </div>
            <div className="font-bold text-gray-900 text-sm text-center">{acmg.classification}</div>
            <div className="text-xs text-gray-500">ACMG Score: <span className="font-bold text-gray-900">{acmg.score}</span></div>
          </div>

          {/* Score breakdown */}
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['Very Strong', 'Strong', 'Moderate', 'Supporting'].map((strength) => {
              const items = acmg.criteria.filter((x) => x.strength === strength && x.met);
              const s = strengthStyle(strength);
              return (
                <div key={strength} className="rounded-xl border p-3 text-center" style={{ background: s.bg, borderColor: s.border }}>
                  <div className="text-2xl font-extrabold" style={{ color: s.badge }}>{items.length}</div>
                  <div className="text-xs font-semibold mt-0.5" style={{ color: s.text }}>{strength}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{items.map((x) => x.code).join(', ') || '–'}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Criteria grid */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {acmg.criteria.map((crit) => (
            <CriterionCard key={crit.code} criterion={crit} />
          ))}
        </div>

        {/* Expandable explanation */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
        >
          <span>Evidence Explanation</span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {expanded && (
          <div className="mt-3 p-4 rounded-xl bg-blue-50 border border-blue-100 text-sm text-gray-700 leading-relaxed animate-fade-in">
            {acmg.explanation}
          </div>
        )}
      </div>
    </section>
  );
}
